import { useEffect, useState } from 'react'
import styles from './EditFrameMenu.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { TrashIcon, ImageIcon, CameraIcon, SidebarIcon, XCircleIcon } from '@phosphor-icons/react'
import {
    deleteFrame,
    updateAllFramesBackgroundImage,
    updateFrameBackgroundColor,
    updateFrameBackgroundImage,
    updateFrameSize,
} from '../../../store/actions'
import { SizePicker } from '../components/SizePicker/SizePicker'
import { setShowFrameDrawer } from '../../../store/actions/uiActions'
import { useModal, MODALS } from '../../modals'
import html2canvas from 'html2canvas'
import sha256 from 'crypto-js/sha256'
import { useParams } from 'react-router-dom'
import { useApi } from '../../../api/api'
import { useDropzone } from 'react-dropzone'
import {
    ScoopButton,
    ScoopDialog,
    ScoopColorPicker,
    ScoopSpinner,
    ScoopTooltip,
    ScoopCheckbox,
    ScoopText,
    ScoopCircularProgress,
    ScoopDropzone,
    useToast,
} from '../../ui'

const imageS3Bucket = 'https://d3lkv74tdz6isd.cloudfront.net/'

// when user uploads an image to S3, make sure the filename is safe
function safeS3FileName(str) {
    return str.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/ /g, '_')
}

export const EditFrameMenu = ({ frame, handleDelete }) => {
    const toast = useToast()
    const objects = useSelector((state) => state.objects)
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const canvasName = useSelector((state) => state.auth.canvasName)
    const zoom = useSelector((state) => state.ui.zoom)
    const lastSaved = useSelector((state) => state.ui.lastSaved)
    const backgroundColor = useSelector((state) => state.ui.backgroundColor || '#ffffff')
    const presentationID = useSelector((state) => state.ui.presentationID)
    const { canvasID } = useParams()
    const dispatch = useDispatch()
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const { postData } = useApi(
        'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/canvasV2'
    )
    const { postData: postDataWorkspaceactions } = useApi(
        'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/workspaceactions'
    )
    const [thumbLoading, setThumbLoading] = useState(false)
    const [backgroundModalOpen, setBackgroundModalOpen] = useState(false)
    const [progress, setProgress] = useState(0)
    const [imageUrl, setImageUrl] = useState('')
    const [applyToAllFrames, setApplyToAllFrames] = useState(false)

    useEffect(() => {
        let timer
        if (progress > 0 && progress < 100) {
            timer = setInterval(() => {
                setProgress((prevProgress) => Math.min(prevProgress + 3, 100))
            }, 1000)
        } else if (progress >= 100) {
            clearInterval(timer)
        }
        return () => {
            clearInterval(timer)
        }
    }, [progress])

    const {
        acceptedFiles,
        fileRejections,
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        accept: {
            'image/png': [],
            'image/jpeg': [],
            'image/gif': [],
            'image/jpg': [],
            'image/svg+xml': [],
        },
    })

    const handleDeleteFrame = () => {
        handleDelete()
        dispatch(deleteFrame(frame.id))
    }

    const captureThumbnail = () => {
        setThumbLoading(true)
        const frameElement = document.getElementById(`FrameElement-${frame.id}`)
        const scoopCanvas = document.getElementById('scoop-canvas')
        const canvasScrollable = document.getElementById('canvas-scrollable')
        if (!frameElement || !scoopCanvas) return
        const frameX = frame.x + canvasScrollable.scrollLeft
        const frameY = frame.y - 115 + canvasScrollable.scrollTop
        const frameWidth = frame.width
        const frameHeight = frame.height
        html2canvas(scoopCanvas, {
            useCORS: true,
            allowTaint: true,
            x: frameX * zoom,
            y: frameY * zoom,
            width: frameWidth * zoom,
            height: frameHeight * zoom,
        })
            .then((capturedCanvas) => {
                let thumbnail
                const scaleWidthLimit = 1920
                // perform image resize if it is too big
                if (capturedCanvas.width > scaleWidthLimit) {
                    const canvasAspectRatio = capturedCanvas.width / capturedCanvas.height
                    const tempCanvas = document.createElement('canvas')
                    const newWidth = scaleWidthLimit
                    const newHeight = scaleWidthLimit / canvasAspectRatio
                    tempCanvas.setAttribute('width', newWidth)
                    tempCanvas.setAttribute('height', newHeight)
                    const ctx = tempCanvas.getContext('2d')
                    ctx.drawImage(
                        capturedCanvas,
                        0,
                        0,
                        capturedCanvas.width,
                        capturedCanvas.height,
                        0,
                        0,
                        newWidth,
                        newHeight
                    )
                    thumbnail = tempCanvas.toDataURL('image/png')
                } else {
                    thumbnail = capturedCanvas.toDataURL('image/png')
                }
                const checksum = sha256(JSON.stringify(objects)).toString()
                const action = {
                    action: 'saveCanvas',
                    userID: userID,
                    workspaceID: workspaceID,
                    canvasID: canvasID,
                    canvasName: canvasName,
                    canvasObjects: objects,
                    canvasImage: thumbnail,
                    zoom: zoom,
                    lastSaved: lastSaved,
                    background: backgroundColor,
                    presentationID: presentationID,
                    checksum: checksum,
                    isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
                }
                postData(action).then(() => {
                    toast.success('Thumbnail generated successfully!')
                    setThumbLoading(false)
                })
            })
            .catch((error) => console.error('Error capturing frame:', error))
    }

    const handleUpload = async () => {
        setProgress(1)
        let payload = {
            action: 'uploadImage',
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        try {
            const presignedData = await postDataWorkspaceactions({
                ...payload,
                fileName: safeS3FileName(acceptedFiles[0].name),
                fileType: acceptedFiles[0].type,
            })
            await fetch(presignedData.signedRequest, {
                method: 'PUT',
                body: acceptedFiles[0],
                headers: { 'Content-Type': acceptedFiles[0].type },
            })
            setProgress(100)

            const imageS3Path = `${process.env.REACT_APP_SCOOP_ENV === 'dev' ? `${imageS3Bucket}dev/${workspaceID}/` : `${imageS3Bucket}${workspaceID}/`}${safeS3FileName(acceptedFiles[0].name)}`

            setImageUrl(imageS3Path)
        } catch (error) {
            console.error('Error uploading the file:', error)
            setProgress(0) // Reset the progress
        }
    }

    const handleSave = () => {
        if (applyToAllFrames) {
            dispatch(updateAllFramesBackgroundImage(imageUrl))
        } else {
            dispatch(updateFrameBackgroundImage(frame.id, imageUrl))
        }
        setBackgroundModalOpen(false)
        setImageUrl('')
    }

    const backgroundActions = (
        <div className={styles.dialogActions}>
            <ScoopButton
                onClick={() => {
                    setBackgroundModalOpen(false)
                    setImageUrl('')
                }}
                variant="grey"
            >
                Cancel
            </ScoopButton>
            <ScoopButton
                onClick={imageUrl ? handleSave : handleUpload}
                disabled={acceptedFiles.length === 0}
            >
                {imageUrl ? 'Save' : 'Upload'}
            </ScoopButton>
        </div>
    )

    return (
        <>
            <div className={styles.frameMenu} id={'frame-menu'}>
                <ScoopColorPicker
                    height={20}
                    width={20}
                    value={frame.backgroundColor}
                    origin={'top'}
                    onChange={(color) => dispatch(updateFrameBackgroundColor(frame.id, color))}
                    tooltip
                />
                <ScoopTooltip title={'Background image'}>
                    <ScoopButton variant="text" onClick={() => setBackgroundModalOpen(true)}>
                        <ImageIcon size={20} />
                    </ScoopButton>
                </ScoopTooltip>
                {frame.backgroundImage && (
                    <ScoopTooltip title={'Remove background image'}>
                        <ScoopButton
                            variant="text"
                            onClick={() => dispatch(updateFrameBackgroundImage(frame.id, ''))}
                        >
                            <XCircleIcon size={22} />
                        </ScoopButton>
                    </ScoopTooltip>
                )}
                <SizePicker
                    origin={'top'}
                    value={{ width: frame.width, height: frame.height }}
                    onChange={(size) => dispatch(updateFrameSize(frame.id, size))}
                    tooltip
                />
                <div className={styles.divider} />
                <ScoopTooltip title={'Manage frames'}>
                    <ScoopButton variant="text" onClick={() => dispatch(setShowFrameDrawer(true))}>
                        <SidebarIcon size={20} />
                    </ScoopButton>
                </ScoopTooltip>
                <ScoopTooltip title={'Set as thumbnail'}>
                    {thumbLoading ? (
                        <ScoopSpinner />
                    ) : (
                        <ScoopButton variant="text" onClick={captureThumbnail}>
                            <CameraIcon size={20} />
                        </ScoopButton>
                    )}
                </ScoopTooltip>
                <div className={styles.divider} />
                <ScoopTooltip title={'Delete'}>
                    <ScoopButton
                        variant="text"
                        onClick={() =>
                            deleteModal.show({
                                title: 'Frame',
                                type: 'frame',
                                onDelete: handleDeleteFrame,
                            })
                        }
                    >
                        <TrashIcon size={20} color="#E50B54" />
                    </ScoopButton>
                </ScoopTooltip>
                <ScoopDialog
                    open={backgroundModalOpen}
                    onClose={() => {
                        setBackgroundModalOpen(false)
                        setImageUrl('')
                    }}
                    title={imageUrl ? 'Preview' : 'Frame background'}
                    actions={backgroundActions}
                >
                    {imageUrl ? (
                        <div>
                            <div
                                className={styles.previewImageContainer}
                                style={{ backgroundImage: `url(${imageUrl})` }}
                            />
                            <div className={styles.checkboxContainer}>
                                <ScoopCheckbox
                                    size="sm"
                                    checked={applyToAllFrames}
                                    onChange={() => setApplyToAllFrames(!applyToAllFrames)}
                                />
                                <ScoopText className={styles.checkboxLabel}>
                                    Apply to all frames
                                </ScoopText>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <ScoopDropzone
                                getRootProps={getRootProps}
                                getInputProps={getInputProps}
                                isFocused={isFocused}
                                isDragAccept={isDragAccept}
                                isDragReject={isDragReject}
                                acceptedFiles={acceptedFiles}
                                mainText="Drag 'n' drop your image file here or"
                                linkText="click to select an image file"
                                hint="(Only .PNG, .GIF, .SVG and .JPG files are supported)"
                            />
                            <div className={styles.progressContainer}>
                                <ScoopCircularProgress value={progress} variant="determinate" />
                            </div>
                        </div>
                    )}
                </ScoopDialog>
            </div>
        </>
    )
}
